/**
 * apps/web/src/components/RouteErrorPage.tsx
 * -------------------------------------------------------
 * Purpose:
 *   Shared error UI for route loader/action failures.
 *
 * High-level data flow:
 *   - React Router renders this element when a loader/action throws.
 *   - We display a safe message and (in dev) provide additional context.
 */

import React from 'react';
import { Alert, Box, Button, Group, Stack, Text, Title } from '@mantine/core';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

export function RouteErrorPage(): React.ReactElement {
  /** React Router error value thrown by a loader/action. */
  const error = useRouteError();

  /** Navigate helper for returning to a safe route. */
  const navigate = useNavigate();

  /** User-safe title and message. */
  let title = 'Something went wrong';
  let message = 'An unexpected error occurred.';

  if (isRouteErrorResponse(error)) {
    title = `Request failed (${error.status})`;
    message = typeof error.data === 'string' ? error.data : message;
  } else if (error instanceof Error) {
    message = error.message;
  }

  return (
    <Box p="md">
      <Stack gap="md" maw={720}>
        <Title order={2}>{title}</Title>

        <Alert title="Error" color="red">
          <Text>{message}</Text>
        </Alert>

        <Group>
          <Button variant="default" onClick={() => navigate(-1)}>
            Go back
          </Button>
          <Button onClick={() => navigate('/')}>Go to dashboard</Button>
        </Group>

        {import.meta.env.DEV && error instanceof Error && error.stack ? (
          <Box>
            <Text size="xs" c="dimmed">
              Debug stack (dev only)
            </Text>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{error.stack}</pre>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}
